import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './ContentTile.module.css';
import { motion, useAnimationControls } from 'framer-motion';
import { SitePage } from '../../Model/siteMap';
import SiteTag from './SiteTag';

const BLOCK_DURATION = 0.2;
const CONTENT_WIPE_DELAY_S = 0.12;
const SLIDE_HOLD_MS = 3000;
const WIPE_DURATION_MS = 450;
/** One Ken Burns keyframe cycle: hold + wipe (outgoing must still zoom during wipe). */
const KB_CYCLE_MS = SLIDE_HOLD_MS + WIPE_DURATION_MS;
/**
 * After a wipe, the new back image must continue Ken Burns from the same progress the incoming
 * image had at handoff (WIPE / KB_CYCLE), while still ending the cycle at wall time KB_CYCLE so
 * the next wipe lines up. With linear keyframes: delay = -D * WIPE / KB_CYCLE and end at delay + D = KB_CYCLE.
 */
const KB_BACK_RESUME_DURATION_MS = Math.round((KB_CYCLE_MS * KB_CYCLE_MS) / SLIDE_HOLD_MS);
const KB_BACK_RESUME_DELAY_MS = Math.round(
    (-KB_BACK_RESUME_DURATION_MS * WIPE_DURATION_MS) / KB_CYCLE_MS,
);
/** Fraction of the observed element's height that must overlap the viewport vertically to run the slideshow. */
const SLIDESHOW_MIN_VISIBLE_HEIGHT_FRAC = 0.22;
const SLIDESHOW_MIN_VISIBLE_HEIGHT_PX = 56;
/** IntersectionObserver needs multiple thresholds so tall tiles get callbacks while scrolling (not only at 0% and 100%). */
const SLIDESHOW_IO_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);
const DESCRIPTION_PREVIEW_MAX = 200;

function isHttpUrl(href: string): boolean {
    return /^https?:\/\//i.test(href);
}

/** True if any part of the element intersects the viewport (first paint / scroll snapshot). */
function isRoughlyOnScreen(el: HTMLElement | null): boolean {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
}

/**
 * Slideshow coordination:
 * Only one ContentTile should animate its slideshow at a time (Ken Burns + wipe cadence).
 */
let activeSlideshowOwnerKey: string | null = null;
const slideshowOwnerSubscribers = new Set<(ownerKey: string | null) => void>();

function setActiveSlideshowOwnerKey(next: string | null) {
    activeSlideshowOwnerKey = next;
    slideshowOwnerSubscribers.forEach((cb) => cb(next));
}

function tryAcquireSlideshowOwner(ownerKey: string): boolean {
    if (activeSlideshowOwnerKey === ownerKey) return true;
    if (activeSlideshowOwnerKey !== null) return false;
    setActiveSlideshowOwnerKey(ownerKey);
    return true;
}

function releaseSlideshowOwner(ownerKey: string) {
    if (activeSlideshowOwnerKey !== ownerKey) return;
    setActiveSlideshowOwnerKey(null);
}

function subscribeSlideshowOwner(cb: (ownerKey: string | null) => void) {
    slideshowOwnerSubscribers.add(cb);
    cb(activeSlideshowOwnerKey);
    return () => {
        slideshowOwnerSubscribers.delete(cb);
    };
}

/** Decode next slide bitmap before the wipe so the clip animation shows real pixels, not a late swap. */
function preloadImage(src: string): Promise<void> {
    return new Promise((resolve) => {
        const img = new Image();
        const done = () => resolve();
        img.onload = () => {
            if (typeof img.decode === 'function') {
                img.decode().then(done).catch(done);
            } else {
                done();
            }
        };
        img.onerror = done;
        img.src = src;
    });
}

interface ContentTileProps {
    page: SitePage;
    phase: 'in' | 'out';
    transitionToken: number;
    onHideComplete: (pageId: string, token: number) => void;
    onOpenDetail?: (page: SitePage) => void;
}

const ContentTile: React.FC<ContentTileProps> = ({
    page,
    phase,
    transitionToken,
    onHideComplete,
    onOpenDetail,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageWrapRef = useRef<HTMLDivElement>(null);

    const imageBlockerControls = useAnimationControls();
    const contentBlockerControls = useAnimationControls();

    const [assetReady, setAssetReady] = useState(false);
    const [imageOpacity, setImageOpacity] = useState(0);
    const [contentOpacity, setContentOpacity] = useState(0);

    const cancelPaintRef = useRef(false);
    const completedHideTokenRef = useRef<number | null>(null);

    const imageUrls = useMemo(
        () => page.images.map((name) => `/images/${page.id}/${name}`),
        [page.id, page.images],
    );
    const imageCount = imageUrls.length;

    const [inView, setInView] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [incomingIndex, setIncomingIndex] = useState<number | null>(null);
    const activeIndexRef = useRef(0);
    const nextSlideTimerRef = useRef<number | null>(null);
    const wipeCommitTimerRef = useRef<number | null>(null);

    const [imageAspectRatio, setImageAspectRatio] = useState<string | null>(null);

    const onAssetSettled = useCallback(() => {
        setAssetReady(true);
    }, []);

    const onFirstImageLoaded = useCallback(
        (event: React.SyntheticEvent<HTMLImageElement>) => {
            onAssetSettled();

            if (imageAspectRatio !== null) return;
            if (activeIndexRef.current !== 0) return;

            const img = event.currentTarget;
            const { naturalWidth, naturalHeight } = img;
            if (!naturalWidth || !naturalHeight) return;
            setImageAspectRatio(`${naturalWidth}/${naturalHeight}`);
        },
        [imageAspectRatio, onAssetSettled],
    );

    const ownerKey = page.id;
    const [globalSlideshowOwnerKey, setGlobalSlideshowOwnerKey] = useState<string | null>(
        () => activeSlideshowOwnerKey,
    );

    useEffect(() => {
        return subscribeSlideshowOwner((nextOwnerKey) => {
            setGlobalSlideshowOwnerKey(nextOwnerKey);
        });
    }, []);

    // Owner + trigger + entrance visibility.
    const slideShowActive =
        inView && phase === 'in' && globalSlideshowOwnerKey === ownerKey && imageOpacity > 0;
    const slideShowActiveRef = useRef(slideShowActive);
    slideShowActiveRef.current = slideShowActive;

    const clearNextSlideTimer = useCallback(() => {
        if (nextSlideTimerRef.current !== null) {
            window.clearTimeout(nextSlideTimerRef.current);
            nextSlideTimerRef.current = null;
        }
    }, []);

    const clearSlideTimers = useCallback(() => {
        clearNextSlideTimer();
        if (wipeCommitTimerRef.current !== null) {
            window.clearTimeout(wipeCommitTimerRef.current);
            wipeCommitTimerRef.current = null;
        }
    }, [clearNextSlideTimer]);

    const scheduleNextSlide = useCallback(() => {
        clearNextSlideTimer();
        if (imageCount < 2 || !slideShowActive) return;
        nextSlideTimerRef.current = window.setTimeout(() => {
            nextSlideTimerRef.current = null;
            if (!slideShowActiveRef.current) return;
            const from = activeIndexRef.current;
            const next = (from + 1) % imageCount;
            const src = imageUrls[next];
            void (async () => {
                await Promise.race([
                    preloadImage(src),
                    new Promise<void>((r) => {
                        window.setTimeout(r, 2500);
                    }),
                ]);
                if (!slideShowActiveRef.current) return;
                if (activeIndexRef.current !== from) return;
                setIncomingIndex(next);
            })();
        }, SLIDE_HOLD_MS);
    }, [clearNextSlideTimer, imageCount, imageUrls, slideShowActive]);

    // Begin fetching sibling slides as soon as the tile is on screen so the first wipe isn't cold.
    useEffect(() => {
        if (!inView || imageCount < 2) return;
        for (let i = 0; i < imageCount; i++) {
            void preloadImage(imageUrls[i]);
        }
    }, [inView, imageCount, imageUrls]);

    // Re-touch the upcoming URL when the active slide changes (belt-and-suspenders with decode).
    useEffect(() => {
        if (!slideShowActive || imageCount < 2) return;
        const nextIndex = (activeIndex + 1) % imageCount;
        void preloadImage(imageUrls[nextIndex]);
    }, [slideShowActive, imageCount, activeIndex, imageUrls]);

    useEffect(() => {
        if (imageCount === 0) {
            setAssetReady(true);
        }
    }, [imageCount]);

    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    useEffect(() => {
        const el = imageWrapRef.current;
        if (!el) return;

        const update = (entry: IntersectionObserverEntry) => {
            if (!entry.isIntersecting) {
                setInView(false);
                return;
            }
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const rect = entry.boundingClientRect;
            const visibleH = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
            const minH = Math.min(
                rect.height,
                Math.max(SLIDESHOW_MIN_VISIBLE_HEIGHT_PX, rect.height * SLIDESHOW_MIN_VISIBLE_HEIGHT_FRAC),
            );
            // Enough of the image area is visible — avoids requiring the whole tile to fit (tall mobile cards).
            const ok = visibleH >= minH;
            setInView(ok);
        };

        const io = new IntersectionObserver(
            (entries) => {
                const e = entries[0];
                if (e) update(e);
            },
            { threshold: SLIDESHOW_IO_THRESHOLDS },
        );

        io.observe(el);
        return () => io.disconnect();
    }, []);

    // Acquire/release the slideshow "token" so only one tile animates at a time.
    useEffect(() => {
        if (phase !== 'in') {
            releaseSlideshowOwner(ownerKey);
            return;
        }

        if (inView) {
            if (globalSlideshowOwnerKey !== ownerKey) {
                tryAcquireSlideshowOwner(ownerKey);
            }
        } else {
            releaseSlideshowOwner(ownerKey);
        }
    }, [globalSlideshowOwnerKey, inView, ownerKey, phase]);

    useEffect(() => {
        return () => {
            releaseSlideshowOwner(ownerKey);
        };
    }, [ownerKey]);

    useEffect(() => {
        if (!slideShowActive) {
            clearSlideTimers();
            setIncomingIndex(null);
            // Reset to the first image when we stop due to scroll (phase still "in").
            // Avoid changing the image while the tile is exiting ("out") during transitions.
            if (phase === 'in') {
                setActiveIndex(0);
                activeIndexRef.current = 0;
            }
            return;
        }
        if (imageCount < 2) return;
        scheduleNextSlide();
        return () => clearSlideTimers();
    }, [clearSlideTimers, imageCount, page.id, phase, scheduleNextSlide, slideShowActive]);

    useEffect(() => {
        if (incomingIndex === null) return;
        wipeCommitTimerRef.current = window.setTimeout(() => {
            wipeCommitTimerRef.current = null;
            setActiveIndex(incomingIndex);
            activeIndexRef.current = incomingIndex;
            setIncomingIndex(null);
            scheduleNextSlide();
        }, WIPE_DURATION_MS);
        return () => {
            if (wipeCommitTimerRef.current !== null) {
                window.clearTimeout(wipeCommitTimerRef.current);
                wipeCommitTimerRef.current = null;
            }
        };
    }, [incomingIndex, scheduleNextSlide]);

    const runRevealPaint = useCallback(
        async (shouldWipe: boolean) => {
            cancelPaintRef.current = false;

            if (!shouldWipe) {
                imageBlockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                contentBlockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                setImageOpacity(1);
                setContentOpacity(1);
                return;
            }

            const imageSequence = async () => {
                await imageBlockerControls.start({
                    scaleX: 1,
                    transformOrigin: 'left center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
                if (cancelPaintRef.current) return;
                setImageOpacity(1);
                await imageBlockerControls.start({
                    scaleX: 0,
                    transformOrigin: 'right center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
            };

            const contentSequence = async () => {
                await new Promise<void>((resolve) =>
                    setTimeout(resolve, CONTENT_WIPE_DELAY_S * 1000),
                );
                if (cancelPaintRef.current) return;
                await contentBlockerControls.start({
                    scaleX: 1,
                    transformOrigin: 'left center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
                if (cancelPaintRef.current) return;
                setContentOpacity(1);
                await contentBlockerControls.start({
                    scaleX: 0,
                    transformOrigin: 'right center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
            };

            await Promise.all([imageSequence(), contentSequence()]);
        },
        [contentBlockerControls, imageBlockerControls],
    );

    const runHidePaint = useCallback(
        async (shouldWipe: boolean, token: number) => {
            cancelPaintRef.current = false;

            if (!shouldWipe) {
                setImageOpacity(0);
                setContentOpacity(0);
                onHideComplete(page.id, token);
                return;
            }

            const imageSequence = async () => {
                imageBlockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                await imageBlockerControls.start({
                    scaleX: 1,
                    transformOrigin: 'left center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
                if (cancelPaintRef.current) return;
                setImageOpacity(0);
                await imageBlockerControls.start({
                    scaleX: 0,
                    transformOrigin: 'right center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
            };

            const contentSequence = async () => {
                await new Promise<void>((resolve) =>
                    setTimeout(resolve, CONTENT_WIPE_DELAY_S * 1000),
                );
                if (cancelPaintRef.current) return;
                contentBlockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                await contentBlockerControls.start({
                    scaleX: 1,
                    transformOrigin: 'left center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
                if (cancelPaintRef.current) return;
                setContentOpacity(0);
                await contentBlockerControls.start({
                    scaleX: 0,
                    transformOrigin: 'right center',
                    transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
                });
            };

            await Promise.all([imageSequence(), contentSequence()]);
            if (!cancelPaintRef.current) {
                onHideComplete(page.id, token);
            }
        },
        [contentBlockerControls, imageBlockerControls, onHideComplete, page.id],
    );

    useEffect(() => {
        if (!assetReady || phase !== 'in') return;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runRevealPaint(shouldWipe);

        return () => {
            cancelPaintRef.current = true;
            imageBlockerControls.stop();
            contentBlockerControls.stop();
        };
    }, [assetReady, contentBlockerControls, imageBlockerControls, phase, runRevealPaint]);

    useEffect(() => {
        if (phase !== 'out') return;

        if (completedHideTokenRef.current === transitionToken) return;
        completedHideTokenRef.current = transitionToken;

        if (!assetReady) {
            setImageOpacity(0);
            setContentOpacity(0);
            onHideComplete(page.id, transitionToken);
            return;
        }

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runHidePaint(shouldWipe, transitionToken);

        return () => {
            cancelPaintRef.current = true;
            imageBlockerControls.stop();
            contentBlockerControls.stop();
        };
    }, [
        assetReady,
        contentBlockerControls,
        imageBlockerControls,
        onHideComplete,
        page.id,
        phase,
        runHidePaint,
        transitionToken,
    ]);

    const handleActivate = useCallback(() => {
        onOpenDetail?.(page);
    }, [onOpenDetail, page]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!onOpenDetail) return;
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpenDetail(page);
            }
        },
        [onOpenDetail, page],
    );

    const handleDemoLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
    }, []);

    const showKenBurns = slideShowActive && imageOpacity > 0;
    const backKenClass =
        imageCount >= 2
            ? showKenBurns
                ? styles.slideImgKenBurns
                : undefined
            : showKenBurns
              ? styles.slideImgKenBurnsLoop
              : undefined;
    const incomingKenClass =
        imageCount >= 2 && showKenBurns && incomingIndex !== null ? styles.slideImgKenBurnsIncoming : undefined;

    const backSrc = imageCount > 0 ? imageUrls[activeIndex] : undefined;
    const incomingSrc = incomingIndex !== null ? imageUrls[incomingIndex] : null;

    const descriptionText = page.description.join(' ');
    const descriptionTruncated = descriptionText.length > DESCRIPTION_PREVIEW_MAX;
    const descriptionPreview = descriptionTruncated
        ? descriptionText.slice(0, DESCRIPTION_PREVIEW_MAX)
        : descriptionText;

    const descriptionInner =
        descriptionTruncated ? (
            <>
                {descriptionPreview}
                {' … '}
                {onOpenDetail ? <span className={styles.readMore}>read more</span> : null}
            </>
        ) : (
            descriptionText
        );

    const tagsRowEl =
        page.tags.length > 0 ? (
            <div className={styles.tagsRow} aria-label="Project tags">
                {page.tags.map((tag, i) => (
                    <SiteTag key={`${page.id}-tag-${i}`} label={tag} variant="compact" />
                ))}
            </div>
        ) : null;

    const slideStackStyle = {
        ['--kb-back' as string]: `${activeIndex > 0 ? KB_BACK_RESUME_DURATION_MS : KB_CYCLE_MS}ms`,
        ['--kb-incoming' as string]: `${KB_CYCLE_MS}ms`,
        ['--wipe-ms' as string]: `${WIPE_DURATION_MS}ms`,
    } as React.CSSProperties;

    const backKenBurnsResumeStyle: React.CSSProperties | undefined =
        imageCount >= 2 && showKenBurns && activeIndex > 0
            ? { animationDelay: `${KB_BACK_RESUME_DELAY_MS}ms` }
            : undefined;

    return (
        <div
            ref={containerRef}
            className={styles.container}
            role={onOpenDetail ? 'button' : undefined}
            tabIndex={onOpenDetail ? 0 : undefined}
            onClick={onOpenDetail ? handleActivate : undefined}
            onKeyDown={onOpenDetail ? handleKeyDown : undefined}
            style={onOpenDetail ? { cursor: 'pointer' } : undefined}
        >
            <div
                className={styles.imageWrap}
                ref={imageWrapRef}
                style={{ aspectRatio: imageAspectRatio ?? '4/3' }}
            >
                <motion.div
                    className={styles.slideshowGate}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: imageOpacity }}
                    transition={{ duration: 0 }}
                >
                    {backSrc ? (
                        <div className={styles.slideStack} style={slideStackStyle}>
                            <div className={`${styles.slideLayer} ${styles.backLayer}`}>
                                <img
                                    key={`kb-${page.id}-${activeIndex}`}
                                    className={`${styles.slideImg}${backKenClass ? ` ${backKenClass}` : ''}`}
                                    style={backKenBurnsResumeStyle}
                                    src={backSrc}
                                    alt={page.title}
                                    onLoad={onFirstImageLoaded}
                                    onError={onAssetSettled}
                                />
                            </div>
                            {incomingSrc ? (
                                <div className={`${styles.slideLayer} ${styles.incomingLayer}`}>
                                    <img
                                        key={`in-${page.id}-${incomingIndex}`}
                                        className={`${styles.slideImg}${incomingKenClass ? ` ${incomingKenClass}` : ''}`}
                                        src={incomingSrc}
                                        alt=""
                                        loading="eager"
                                        decoding="sync"
                                    />
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div className={styles.slideStack} aria-hidden />
                    )}
                </motion.div>
                <motion.div
                    className={styles.blocker}
                    aria-hidden
                    initial={{ scaleX: 0 }}
                    animate={imageBlockerControls}
                    style={{ transformOrigin: 'left center' }}
                />
            </div>
            <div className={styles.contentWrap}>
                <motion.div
                    className={styles.content}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: contentOpacity }}
                    transition={{ duration: 0 }}
                >
                    <div className={styles.title}>{page.title}</div>
                    {page.demoLink ? (
                        <div className={styles.descriptionWithDemo}>
                            <div className={styles.descriptionCol}>
                                <div className={styles.description}>{descriptionInner}</div>
                            </div>
                            {tagsRowEl}
                            <div className={styles.demoLinkCol}>
                                <a
                                    className={styles.demoButton}
                                    href={page.demoLink}
                                    target={isHttpUrl(page.demoLink) ? '_blank' : undefined}
                                    rel={isHttpUrl(page.demoLink) ? 'noopener noreferrer' : undefined}
                                    onClick={handleDemoLinkClick}
                                >
                                    {page.demoLinkTitle ?? 'Open'}
                                </a>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={styles.description}>{descriptionInner}</div>
                            {tagsRowEl}
                        </>
                    )}
                </motion.div>
                <motion.div
                    className={styles.blocker}
                    aria-hidden
                    initial={{ scaleX: 0 }}
                    animate={contentBlockerControls}
                    style={{ transformOrigin: 'left center' }}
                />
            </div>
        </div>
    );
};

export default ContentTile;
